import { useQuery } from '@tanstack/react-query';
import { vendorApi } from '@/services/api';
import { useVendorStore } from '@/store/vendor-store';

export type KycStatus = 'pending' | 'under_review' | 'approved' | 'rejected' | 'suspended';

export interface VendorStatusInfo {
  status: KycStatus;
  rejectionReason: string | null;
  documentReviews: Record<string, { status: 'pending' | 'approved' | 'rejected'; note?: string }>;
  vendor: any | null;
  isLoading: boolean;
}

/**
 * Single source of truth for the logged-in vendor's verification state.
 *
 * Reads the live `/vendors/my-vendor` record (shared cache key with the Store
 * page) and falls back to the value captured in the auth store at login. This
 * powers both the dashboard approval gate and the Pending screen so a vendor
 * who gets approved (or re-submits) sees the change without a full re-login.
 */
export function useVendorStatus(): VendorStatusInfo {
  const fallback = useVendorStore((s) => s.vendor?.kycStatus) as KycStatus | undefined;

  const { data, isLoading } = useQuery({
    queryKey: ['vendor', 'me'],
    queryFn: async () => {
      const res = await vendorApi.getMyVendor();
      return (res.data?.data ?? null) as any;
    },
    staleTime: 30_000,
  });

  const status = (data?.verificationStatus ?? fallback ?? 'pending') as KycStatus;

  return {
    status,
    rejectionReason: data?.rejectionReason ?? null,
    documentReviews: data?.documentReviews ?? {},
    vendor: data ?? null,
    // Only "loading" when we have nothing at all to decide on yet.
    isLoading: isLoading && !data && !fallback,
  };
}
